import { FieldSubject, IncomeOption } from '@hb/domain';
import { ApplicantsStoreService } from '@hb/modules/applicants/applicants-store.service';
import { normalizeToUTCMidnight } from '@hb/modules/applicants/applicants.constants';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, skip } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DateOfBirthStoreService extends AbstractStoreInjectOption<IncomeOption> {
    protected readonly store = inject(ApplicantsStoreService);
    protected readonly lastQuestion = IncomeOption.DATE_OF_BIRTH;

    private readonly mainApplicantDateOfBirthSubject: FieldSubject<Date>;
    private readonly coApplicantDateOfBirthSubject: BehaviorSubject<Date>;

    isLoading = false;

    constructor() {
        super();
        this.init();

        const initialValues = this.getInitialValues();
        this.mainApplicantDateOfBirthSubject = new FieldSubject<Date>(
            initialValues?.main_applicant?.date_of_birth != null ? new Date(initialValues?.main_applicant?.date_of_birth) : null
        );
        this.coApplicantDateOfBirthSubject = new BehaviorSubject<Date>(
            initialValues?.co_applicant?.date_of_birth != null ? new Date(initialValues?.co_applicant?.date_of_birth) : null
        );
        this.subscriptions();
    }

    getInitialValues() {
        return this.store.getApplicantsState();
    }

    private subscriptions(): void {
        this.mainApplicantDateOfBirthSubject.asObservable().subscribe(() => this.update());
        this.coApplicantDateOfBirthSubject.asObservable().subscribe(() => this.update());
        this.store.getIsLoading().subscribe((isLoading) => (this.isLoading = isLoading));
        this.getHasCoApplicant()
            .pipe(distinctUntilChanged(), skip(1))
            .subscribe((hasCoApplicant) => {
                if (!hasCoApplicant) {
                    this.setCoApplicantDateOfBirth(null);
                }
            });
    }

    getMainApplicantDateOfBirth(): Date {
        return this.mainApplicantDateOfBirthSubject.value !== null ? new Date(this.mainApplicantDateOfBirthSubject.value) : null;
    }

    getCoApplicantDateOfBirth(): Date {
        return this.coApplicantDateOfBirthSubject.value !== null ? new Date(this.coApplicantDateOfBirthSubject.value) : null;
    }

    setMainApplicantDateOfBirth(date: Date): void {
        this.mainApplicantDateOfBirthSubject.next(normalizeToUTCMidnight(date));
    }

    setCoApplicantDateOfBirth(date: Date): void {
        this.coApplicantDateOfBirthSubject.next(normalizeToUTCMidnight(date));
    }

    update(): void {
        this.store.setMainApplicantDateOfBirth(this.mainApplicantDateOfBirthSubject.value);
        this.store.setCoApplicantDateOfBirth(this.coApplicantDateOfBirthSubject.value);
    }

    nextQuestion(next: number = 1) {
        if (!this.isLoading) {
            super.nextQuestion(next);
        }
    }
}
